import { useCallback } from 'react';
import { trpc } from '@/lib/trpc';
import { getJupyterUrl } from '@sharpe/shared/jupyter-url';
import type { JupyterProviderSelection } from '@sharpe/shared/server';
import { useActiveProviderInfo } from '@/hooks/use-active-provider-info';

interface UseOpenJupyterResult {
  openJupyter: (workspaceDir: string, notebookName?: string) => Promise<void>;
  isLoading: boolean;
}

/**
 * Opens JupyterLab in a new tab. For self-hosted providers, opens directly via
 * localhost. For Fly.io, fetches (and if necessary starts) the sandbox first.
 */
export function useOpenJupyter(selectionOverride?: JupyterProviderSelection): UseOpenJupyterResult {
  const getSandboxUrl = trpc.jupyter.getSandboxUrl.useMutation();
  const { data: providerInfo, preferredProviderSelection } = useActiveProviderInfo(
    selectionOverride ? { selectionOverride } : undefined
  );

  const openJupyter = useCallback(
    async (workspaceDir: string, notebookName?: string) => {
      if (providerInfo?.providerType === 'self-hosted' && providerInfo.localPort) {
        const baseUrl = `http://localhost:${providerInfo.localPort}`;
        window.open(getJupyterUrl(baseUrl, workspaceDir, notebookName), '_blank');
        return;
      }

      if (preferredProviderSelection.type === 'self-hosted') {
        return;
      }

      const result = await getSandboxUrl.mutateAsync();
      if (result.sandboxUrl) {
        window.open(getJupyterUrl(result.sandboxUrl, workspaceDir, notebookName), '_blank');
      }
    },
    [getSandboxUrl, preferredProviderSelection.type, providerInfo]
  );

  return { openJupyter, isLoading: getSandboxUrl.isPending };
}
